<template>
	<div
		class="dialog"
		:class="{ 'is-active' : internalVisible }"
		:tabindex="internalVisible ? '0' : '-1'"
	>
		<el-dialog
			ref="dialog"
			:class="[
				{
					'dialog--confirm' : confirmVisible,
					'is-loading' : isLoading,
					'dialog--sticky-header' : headerIsSticky,
					'dialog--custom-width' : maxWidth
				},
				customWrapperClass
			]"
			:visible="internalVisible"
			:width="width"
			:custom-class="customClass"
			:destroy-on-close="destroyOnClose"
			:before-close="handleClose"
			:close-on-click-modal="closeOnClickModal"
			:append-to-body="true"
			:show-close="false"
			:style="`--max-width: ${maxWidth}; ${customStyles}`"
			:data-testid="dataTestid"
			v-on="$listeners"
			@opened="handleOpened"
		>
			<div v-if="isLoading" class="dialog__loading-indicator flex flex--center">
				<Inline svg="loading" class="spin white" />
			</div>
			<template #title>
				<button
					v-if="showClose"
					ref="closeButton"
					aria-label="Close"
					class="dialog__close-button button--no-style text-icon-subtle"
					@click="handleClose()"
				>
					<Inline svg="ex" />
				</button>
				<div v-if="$slots.headerImage" class="dialog__header-image flex flex--align">
					<slot name="headerImage" />
				</div>
				<div class="flex flex-col">
					<h1
						v-if="title"
						class="dialog__title mt-0 mr-2 mb-0"
						:class="[{'print:hidden': hideTitleFromPrint}]"
					>
						{{ title }}
					</h1>
					<slot name="title-helptext" />
				</div>
				<div v-if="$slots.headerButton" class="dialog__header-button">
					<slot name="headerButton" />
				</div>
			</template>
			<div v-show="!isLoading" class="dialog__content-container">
				<slot />
			</div>
			<div v-show="!isLoading" class="mt dialog__button-bar">
				<div class="dialog__buttons-left">
					<slot name="buttons-left" />
				</div>
				<div v-if="$slots.footer" class="dialog__footer">
					<slot name="footer" />
				</div>
			</div>
		</el-dialog>
	</div>
</template>

<script>
	import Vue from 'vue';
	import { Dialog } from '@cognitoforms/element-ui';
	import trapFocus from '../../../src/mixins/trap-focus';
	import confirm from '../../utilities/confirm';
	import { handleDialogVisibility, setupChromeOverlayClickHandler } from './dialog-util';
	import clickOutside from '../../utilities/clickOutside';

	Vue.directive('click-outside', clickOutside);
	Vue.use(Dialog);

	export default {
		name: 'CDialog',
		mixins: [trapFocus],
		props: {
			title: { type: String, default: '' },
			visible: Boolean,
			showClose: { type: Boolean, default: true }, // Show X at the top of the dialog
			isLoading: { type: Boolean, default: false }, // Show loading indicator instead of Modal
			width: { type: String, default: '96%' },
			maxWidth: { type: String, default: null },
			customClass: { type: String, default: '' },
			customWrapperClass: { type: String, default: '' },
			destroyOnClose: Boolean,
			showConfirm: { type: Boolean, default: false },
			confirmOptions: { type: Object, default: null },
			closeOnClickModal: { type: Boolean, default: true }, // Close when clicking on the blurred area
			headerIsSticky: { type: Boolean, default: false },
			closeOnRouteChange: { type: Boolean, default: true },
			handleCloseExternally: { type: Boolean, default: false },
			hideTitleFromPrint: { type: Boolean, default: false },
			dataTestid: { type: String, default: null },
			customStyles: { type: String, default: '' }
		},
		emits: ['done', 'closed', 'close', 'handle-close'],
		data() {
			return {
				lastFocusedEl: null,
				internalVisible: this.visible,
				confirmVisible: false
			};
		},
		watch: {
			visible(val) {
				this.internalVisible = val;
			},
			internalVisible: {
				immediate: true,
				handler: function(value) {
					handleDialogVisibility(value);
				}
			},
			'$route'() {
				if (this.visible && this.closeOnRouteChange) {
					this.$emit('closed');
					this.$emit('close');
				}
			}
		},
		mounted() {
			window.addEventListener('blur', this.clickOutside);

			if (this.$refs.dialog)
				this.$refs.dialog.$el.addEventListener('keydown', this.handleKeydown);
			setupChromeOverlayClickHandler(() => this.handleClose());

			this.$nextTick(() => {
				if (this.visible)
					this.handleOpened();
			});
		},
		beforeDestroy() {
			// If the dialog is conditionally mounted, we must make sure
			// the overlay is removed when destroying
			handleDialogVisibility(false);
			window.removeEventListener('blur', this.clickOutside);
		},
		methods: {
			clickOutside(event) {
				const className = event?.target?.className;
				if (this.internalVisible && typeof className === 'string' && className.includes('el-dialog__wrapper') && this.closeOnClickModal) {
					this.handleClose();
				}
			},
			handleKeydown(e) {
				if (this.$refs.dialog)
					this.trapFocus(e, this.$refs.dialog.$el);
			},
			handleOpened() {
				// Record what element had focus in order to return to that on close.
				this.lastFocusedEl = document.activeElement;

				// Put focus on the close button of the modal if no other element is currently active within the modal
				// so you can't tab backwards outside of the modal.
				if (!this.$refs.dialog.$el.contains(document.activeElement))
					this.$refs.closeButton.focus();
			},
			handleClose() {
				if (this.handleCloseExternally) {
					this.$emit('handle-close');
				}
				else {
					if (this.showConfirm) {
						if (!this.confirmVisible) {
							// close dialog if user confirms
							this.confirmVisible = true;
							confirm(this.confirmOptions).then(() => {
								this.internalVisible = false;
								this.confirmVisible = false;
								this.$emit('done');
							}).catch(() => {
								this.confirmVisible = false;
							});
						}
					}
					else {
						this.internalVisible = false;

						this.$emit('done');
					}
				}
			}
		}
	};
</script>

<style lang="scss" src="@cognitoforms/styles/global/_motion.scss"></style>

<style lang="scss">
.dialog {

	&--wide,
	&--12 {
		max-width: 1200px;
	}

	&--medium,
	&--8 {
		max-width: 800px;
	}

	&--6 {
		max-width: 650px;
	}

	&--narrow,
	&--5 {
		max-width: 500px;
	}

	&--tiny,
	&--4 {
		max-width: 450px;
	}

	&--custom-width .el-dialog {
		max-width: var(--max-width);
	}

	&__header-image {
		flex-shrink: 0;

		.icon,
		img {
			width: $size__icon--xl;
			height: $size__icon--xl;
		}

		.i-simple {
			// Intentionally deviating from standards
			color: var(--shade-10);
		}
	}

	&__header-button {
		position: absolute;
		right: var(--spacing-5);
	}

	&--confirm .el-dialog__body {
		pointer-events: none;

		&::before {
			position: absolute;
			left: 0;
			top: 0;
			/* stylelint-disable-next-line scale-unlimited/declaration-strict-value */
			z-index: $z20;
			width: 100%;
			height: 100%;
			background-color: var(--bg-overlay-subtle);
			content: '';
		}
	}

	html:not(.website) &__title {
		margin-right: 0 !important;
		font-size: $size__6;
	}

	&__loading-indicator {
		opacity: 0;
		animation: fade-in 1s 200ms forwards;

		:root & .i-loading {
			width: 40px;
			height: auto;
		}
	}

	&__buttons-left {
		display: flex;
		position: absolute;
		flex-wrap: wrap;
		margin-top: 10px;
		margin-right: 55px;
	}

	&__footer {
		display: flex;
		align-items: flex-start;
		flex-wrap: wrap;
		justify-content: flex-end;
		width: calc(100% + #{$gutter});
		margin-top: 2rem;
		margin-left: $gutter * -1;
		padding-left: 2rem;
		gap: $space__4;
		page-break-inside: avoid;

		.button {
			font-size: $size__3;
		}
	}
}

// Modals are centered with flex. Extra padding on the bottom serves two purposes:
// 1. On mobile, gives extra dismissable space at the bottom (useful when no cancel button is present)
// 2. Bottom weights the modal. https://www.archivalmethods.com/blog/matting-part-4-centered-weighted-mats/
.el-dialog__wrapper {
	display: flex;
	position: fixed;
	bottom: 0;
	left: 0;
	right: 0;
	top: 0;
	z-index: $z-modal !important;
	background-color: var(--bg-overlay);
	margin: 0;
	overflow: auto;
	padding-top: $space__4;
	padding-bottom: $space__4;
	animation: fade-in .5s;

	@include scrollbar($wide-scrollbar);

	@media print {
		position: absolute;
		overflow: visible;
	}

	&:not(.is-loading, .dialog-fade-leave-active) {

		.el-dialog {
			animation: dialog-fade .3s 1ms forwards;
		}
	}

	&:not(.is-loading) {

		.el-dialog {
			background: var(--bg-primary);

			[data-has-custom-theme='true'][data-color-scheme='dark'] & {
				border: 1px solid var(--border-primary);
			}

			@media not print {
				@include modal-shadow;
			}
		}
	}

	&.is-loading {

		.el-dialog {
			box-shadow: unset;
			pointer-events: none;
			opacity: 1;

			&__header {
				display: none;
			}

			&__body {
				background-color: transparent;
			}
		}
	}

	&:not(.dialog--sticky-header) {

		.el-dialog {
			width: calc(100% - #{$space__4}) !important;
		}
	}

	&.dialog--sticky-header {
		padding-top: 0;

		.el-dialog {
			width: calc(100% - $space__4) !important;

			&__header {
				position: sticky;
				top: 0;
				background-color: var(--bg-primary);
				padding-bottom: $space__4;
			}
		}
	}

	@media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
		display: block;
	}
}

.el-dialog {
	position: relative;
	box-sizing: border-box;
	margin: auto !important;

	@media screen {
		opacity: 0;
	}

	.i-ex {
		color: var(--icon-subtle);
		transition: color $speed__hovered;

		@media print {
			display: none;
		}
	}

	&__header {
		display: flex;
		z-index: $z10;
		align-items: flex-start;
		padding: $gutter;
		padding-bottom: $space__4;
		gap: $space__3;
	}

	&__body {
		padding: $gutter;
		padding-top: 0;
	}
}

.dialog__close-button {
	position: absolute;
	right: 0;
	top: 0;
	background: transparent;
	border: 0;
	padding: var(--space__touch-target);
	cursor: pointer;

	html:not(.website) & {

		&:hover .i-ex {
			color: var(--shade-10);
		}
	}

	@include b-down(700px) {
		padding-bottom: var(--spacing-4);
		padding-left: var(--spacing-4);
	}
}

:root:root [data-el-popup-parent--hidden] {
	overflow: hidden;
}

.dialog-fade-leave-active {
	opacity: 0;
	transition: opacity .5s;
}
</style>